import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  DollarSign, 
  Trophy, 
  Flag, 
  TrendingUp, 
  Users, 
  Camera,
  ChevronRight,
  X,
  Medal,
  Calendar as CalendarIcon,
  MapPin,
  Sparkles
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import OutingCreateFlow from "../modules/outings/components/OutingCreateFlow";
import { InProgressGames } from '../components/compete/InProgressGames';
import { useInProgressGames } from '../components/compete/useInProgressGames';

// Skins Rules Modal
const SkinsRulesModal = ({ onClose, onContinue, skinsCount }) => {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-lg animate-fade-in my-4">
        {/* Rules */}
        <CardContent className="p-6 pt-8">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-stone-400 hover:text-stone-600"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-amber-600" />
            </div>
          </div>
          <h3 className="font-bold text-stone-900 mb-6 text-center text-lg">SKINS RULES</h3>
          <div className="space-y-3">
            {[
              { icon: Flag, text: "Lowest score on each hole wins the skin" },
              { icon: TrendingUp, text: "Ties carry over to next hole" },
              { icon: Users, text: "Add players by name or link accounts" },
              { icon: Camera, text: "Submit scorecards for all players" },
              { icon: Trophy, text: "Automatic skins calculation" },
              { icon: DollarSign, text: "Track skin values and totals" }
            ].map((feature, i) => (
              <div key={`skins-rule-${i}`} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-amber-600" />
                </div>
                <span className="text-sm text-stone-700">{feature.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
        
        {/* Actions */}
        <div className="px-6 pb-6 space-y-3">
          <Button 
            onClick={onContinue}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12"
            data-testid="continue-to-skins-btn"
          >
            {skinsCount > 0 ? `View ${skinsCount} Game${skinsCount !== 1 ? 's' : ''}` : 'Create Skins Game'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full text-stone-500"
          >
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Match Play Rules Modal
const MatchPlayRulesModal = ({ onClose, onContinue, matchPlayCount }) => {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-lg animate-fade-in my-4">
        {/* Rules */}
        <CardContent className="p-6 pt-8">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-stone-400 hover:text-stone-600"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Trophy className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="font-bold text-stone-900 mb-6 text-center text-lg">MATCH PLAY FEATURES</h3>
          <div className="space-y-3">
            {[
              { icon: Users, text: "2-man teams compete head-to-head" },
              { icon: Trophy, text: "5 points for win, 2.5 for tie" },
              { icon: TrendingUp, text: "Handicaps calculated after 3 rounds" },
              { icon: CalendarIcon, text: "Flexible weekly 9-hole rounds" },
              { icon: Camera, text: "Scorecard photo, manual, or final score entry" },
              { icon: Flag, text: "Live leaderboard updates" }
            ].map((feature, i) => (
              <div key={`mp-rule-${i}`} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm text-stone-700">{feature.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
        
        {/* Actions */}
        <div className="px-6 pb-6 space-y-3">
          <Button 
            onClick={onContinue}
            className="w-full bg-green-700 hover:bg-green-800 text-white h-12"
            data-testid="continue-to-matchplay-btn"
          >
            {matchPlayCount > 0 ? `View ${matchPlayCount} League${matchPlayCount !== 1 ? 's' : ''}` : 'Create Match Play League'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full text-stone-500"
          >
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Stroke Play Rules Modal
const StrokePlayRulesModal = ({ onClose, onContinue, strokePlayCount }) => {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-lg animate-fade-in my-4">
        <CardContent className="p-6 pt-8">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-stone-400 hover:text-stone-600"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Medal className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="font-bold text-stone-900 mb-6 text-center text-lg">STROKE PLAY FEATURES</h3>
          <div className="space-y-3">
            {[
              { icon: Flag, text: "Track total strokes over 9 or 18 holes" },
              { icon: TrendingUp, text: "Optional handicaps for net scoring" },
              { icon: Users, text: "Add any number of players" },
              { icon: DollarSign, text: "Optional entry fees and payouts" },
              { icon: Trophy, text: "Gross and net leaderboards" },
              { icon: Camera, text: "Manual or photo scorecard entry" }
            ].map((feature, i) => (
              <div key={`sp-rule-${i}`} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm text-stone-700">{feature.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
        
        <div className="px-6 pb-6 space-y-3">
          <Button 
            onClick={onContinue}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
            data-testid="continue-to-strokeplay-btn"
          >
            {strokePlayCount > 0 ? `View ${strokePlayCount} Game${strokePlayCount !== 1 ? 's' : ''}` : 'Create Stroke Play Game'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full text-stone-500"
          >
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
};

const TournamentsPage = ({ currentUser }) => {
  const navigate = useNavigate();
  const progress = useInProgressGames(currentUser?.user_id);
  const matchPlayCount = progress.counts['match-play'] || 0;
  const skinsCount = progress.counts.skins || 0;
  const strokePlayCount = progress.counts['stroke-play'] || 0;
  const [showSkinsRules, setShowSkinsRules] = useState(false);
  const [showMatchPlayRules, setShowMatchPlayRules] = useState(false);
  const [showStrokePlayRules, setShowStrokePlayRules] = useState(false);
  const [showOutingCreate, setShowOutingCreate] = useState(false);
  
  return (
    <div className="ps-theme-page dark-page max-w-4xl mx-auto animate-fade-in p-4" data-testid="tournaments-page">
      
      <InProgressGames key={currentUser?.user_id || 'guest'} {...progress} />
      
      {/* Group Round Card - Most Common */}
      <div 
        className="dark-card overflow-hidden cursor-pointer hover:shadow-xl transition-all mb-6"
        onClick={() => navigate('/add-score', { state: { startGroupRound: true } })}
        data-testid="group-round-link-card"
      >
        <div className="bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-800 text-white p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="w-16 h-16 text-emerald-200" />
          </div>
          <h2 className="text-2xl font-bold mb-2">GROUP ROUND</h2>
          <p className="text-emerald-100 max-w-md mx-auto mb-4">
            Play a casual round with your crew - track scores together with live sync
          </p>
          <div className="inline-flex items-center gap-2 text-emerald-200 font-medium">
            <span>Start Group Round</span>
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Skins Card */}
      <div 
        className="dark-card overflow-hidden cursor-pointer hover:shadow-xl transition-all mb-6"
        onClick={() => setShowSkinsRules(true)}
        data-testid="skins-link-card"
      >
        <div className="bg-gradient-to-br from-amber-600 via-amber-700 to-amber-800 text-white p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <DollarSign className="w-16 h-16 text-yellow-300" />
            {skinsCount > 0 && (
              <span className="px-3 py-1 bg-yellow-300 text-amber-900 rounded-full text-sm font-bold">
                {skinsCount} Game{skinsCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h2 className="text-2xl font-bold mb-2">SKINS GAMES</h2>
          <p className="text-amber-100 max-w-md mx-auto mb-4">
            Traditional skins format - lowest score wins the hole, ties carry over
          </p>
          <div className="inline-flex items-center gap-2 text-yellow-300 font-medium">
            <span>Enter Skins</span>
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Stroke Play Card */}
      <div 
        className="dark-card overflow-hidden cursor-pointer hover:shadow-xl transition-all mb-6"
        onClick={() => setShowStrokePlayRules(true)}
        data-testid="stroke-play-link-card"
      >
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Medal className="w-16 h-16 text-blue-200" />
            {strokePlayCount > 0 && (
              <span className="px-3 py-1 bg-blue-200 text-blue-900 rounded-full text-sm font-bold">
                {strokePlayCount} Game{strokePlayCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h2 className="text-2xl font-bold mb-2">STROKE PLAY</h2>
          <p className="text-blue-200 max-w-md mx-auto mb-4">
            Classic total stroke competition - track gross and net scores with optional handicaps and payouts
          </p>
          <div className="inline-flex items-center gap-2 text-blue-200 font-medium">
            <span>Enter Stroke Play</span>
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Match Play Card */}
      <div 
        className="dark-card overflow-hidden cursor-pointer hover:shadow-xl transition-all mb-6"
        onClick={() => setShowMatchPlayRules(true)}
        data-testid="match-play-link-card"
      >
        <div className="bg-gradient-to-br from-green-800 via-green-900 to-green-950 text-white p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-16 h-16 text-lime-400" />
            {matchPlayCount > 0 && (
              <span className="px-3 py-1 bg-lime-400 text-green-900 rounded-full text-sm font-bold">
                {matchPlayCount} League{matchPlayCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h2 className="text-2xl font-bold mb-2">MATCH PLAY LEAGUES</h2>
          <p className="text-green-200 max-w-md mx-auto mb-4">
            Create head-to-head team competitions with automatic handicap tracking and weekly leaderboards
          </p>
          <div className="inline-flex items-center gap-2 text-lime-300 font-medium">
            <span>Enter Match Play</span>
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Golf Outings Card - Large Event Management */}
      <div 
        className="dark-card overflow-hidden cursor-pointer hover:shadow-xl transition-all"
        onClick={() => navigate('/outings')}
        data-testid="golf-outings-link-card"
      >
        <div className="bg-gradient-to-br from-purple-600 via-violet-700 to-indigo-800 text-white p-8 text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-4 right-4">
            <Sparkles className="w-6 h-6 text-purple-300 animate-pulse" />
          </div>
          <div className="absolute bottom-4 left-4">
            <MapPin className="w-5 h-5 text-purple-300/50" />
          </div>
          
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="w-16 h-16 text-purple-200" />
          </div>
          <h2 className="text-2xl font-bold mb-2">GOLF OUTINGS</h2>
          <p className="text-purple-200 max-w-md mx-auto mb-4">
            Organize large charity events & corporate outings - Scramble, Best Ball, Shamble formats with live leaderboards
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <span className="px-2 py-1 bg-purple-500/30 rounded text-xs text-purple-100">2-Man / 4-Man Teams</span>
            <span className="px-2 py-1 bg-purple-500/30 rounded text-xs text-purple-100">Skins Overlay</span>
            <span className="px-2 py-1 bg-purple-500/30 rounded text-xs text-purple-100">CTP Contests</span>
          </div>
          <div className="inline-flex items-center gap-2 text-purple-200 font-medium">
            <span>Manage Outings</span>
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Skins Rules Modal */}
      {showSkinsRules && (
        <SkinsRulesModal 
          onClose={() => setShowSkinsRules(false)}
          onContinue={() => {
            setShowSkinsRules(false);
            navigate('/tournaments/skins');
          }}
          skinsCount={skinsCount}
        />
      )}

      {/* Match Play Rules Modal */}
      {showMatchPlayRules && (
        <MatchPlayRulesModal 
          onClose={() => setShowMatchPlayRules(false)}
          onContinue={() => {
            setShowMatchPlayRules(false);
            navigate('/tournaments/match-play');
          }}
          matchPlayCount={matchPlayCount}
        />
      )}

      {/* Stroke Play Rules Modal */}
      {showStrokePlayRules && (
        <StrokePlayRulesModal 
          onClose={() => setShowStrokePlayRules(false)}
          onContinue={() => {
            setShowStrokePlayRules(false);
            navigate('/tournaments/stroke-play');
          }}
          strokePlayCount={strokePlayCount}
        />
      )}
      
      {/* Outing Create Flow Popup */}
      <OutingCreateFlow 
        isOpen={showOutingCreate}
        onClose={() => setShowOutingCreate(false)}
        user={currentUser}
      />
    </div>
  );
};

export default TournamentsPage;
